import {
  COMISION_PCT_POR_NIVEL,
  DEUDA_ARS_BLOQUEO,
  MAX_VENTAS_IMPAGAS_BLOQUEO,
  UMBRAL_NIVEL_2,
  UMBRAL_NIVEL_3,
  UMBRAL_NIVEL_4,
  UMBRAL_NIVEL_5,
  VENTANA_VENTAS_DIAS,
} from '../../../core/monetizacion/vendedorNiveles.js';

/**
 * @param {number} ventasEnVentana - cantidad de ventas válidas en los últimos 30 días
 * @returns {1|2|3|4|5}
 */
export function nivelDesdeVentasEnVentana(ventasEnVentana) {
  const n = Math.max(0, Math.floor(Number(ventasEnVentana) || 0));
  if (n >= UMBRAL_NIVEL_5) return 5;
  if (n >= UMBRAL_NIVEL_4) return 4;
  if (n >= UMBRAL_NIVEL_3) return 3;
  if (n >= UMBRAL_NIVEL_2) return 2;
  return 1;
}

/** Firestore puede devolver números o Timestamp en `ventas_ts_window_ms`. */
export function normalizarVentasTsWindowMs(arr) {
  if (!Array.isArray(arr)) return [];
  const out = [];
  for (const t of arr) {
    if (typeof t === 'number' && Number.isFinite(t)) out.push(t);
    else if (t != null && typeof t.toMillis === 'function') {
      const m = t.toMillis();
      if (Number.isFinite(m)) out.push(m);
    }
  }
  return out;
}

/**
 * Recorta timestamps de ventas fuera de la ventana y agrega una nueva (ms).
 * @param {number[]} ventasTsMs
 * @param {number} ahoraMs
 * @returns {number[]}
 */
export function ventanaVentasTsActualizada(ventasTsMs, ahoraMs = Date.now()) {
  const ventanaMs = VENTANA_VENTAS_DIAS * 24 * 60 * 60 * 1000;
  const min = ahoraMs - ventanaMs;
  const prev = normalizarVentasTsWindowMs(ventasTsMs).filter((t) => t >= min);
  return [...prev, ahoraMs].sort((a, b) => a - b);
}

/** Solo lectura: cuántas ventas cuentan (sin agregar una nueva). */
export function contarVentasEnVentana(ventasTsMs, ahoraMs = Date.now()) {
  const ventanaMs = VENTANA_VENTAS_DIAS * 24 * 60 * 60 * 1000;
  const min = ahoraMs - ventanaMs;
  return normalizarVentasTsWindowMs(ventasTsMs).filter((t) => t >= min).length;
}

/**
 * Nivel usado para % de comisión al marcar vendido (trial = 5).
 * @param {{ ventasEnVentana: number, enTrialNivel5: boolean }} opts
 */
export function nivelParaComision({ ventasEnVentana, enTrialNivel5 }) {
  if (enTrialNivel5) return 5;
  return nivelDesdeVentasEnVentana(ventasEnVentana);
}

export function porcentajeComisionPorNivel(nivel) {
  const k = Math.min(5, Math.max(1, Math.floor(Number(nivel) || 1)));
  return COMISION_PCT_POR_NIVEL[k] ?? 5;
}

export function montoComisionArs(precio, pct) {
  const p = Number(precio);
  if (!Number.isFinite(p) || p <= 0) return 0;
  const c = (p * Number(pct)) / 100;
  return Math.round(c * 100) / 100;
}

/**
 * @returns {{ bloqueado: boolean, motivo?: string }}
 */
export function evaluarBloqueoPublicar(deudaArs, saldoFavorArs, ventasImpagas) {
  const d = Number(deudaArs) || 0;
  const s = Number(saldoFavorArs) || 0;
  const net = Math.max(0, Math.round((d - s) * 100) / 100);
  const imp = Math.max(0, Math.floor(Number(ventasImpagas) || 0));
  if (net <= 0) return { bloqueado: false };
  if (imp >= MAX_VENTAS_IMPAGAS_BLOQUEO || net > DEUDA_ARS_BLOQUEO) {
    return {
      bloqueado: true,
      motivo:
        imp >= MAX_VENTAS_IMPAGAS_BLOQUEO
          ? `Tenés ${imp} ventas con comisión pendiente. Regularizá la deuda para seguir publicando.`
          : `La deuda supera $ ${DEUDA_ARS_BLOQUEO.toLocaleString('es-AR')} ARS. Regularizá con la plataforma para seguir publicando.`,
    };
  }
  return { bloqueado: false };
}

/**
 * Progreso hacia el siguiente nivel (para barra "Ventas").
 */
export function progresoSiguienteNivel(ventasEnVentana) {
  const v = Math.max(0, Math.floor(Number(ventasEnVentana) || 0));
  const labels = ['Nivel 1', 'Nivel 2', 'Nivel 3', 'Nivel 4', 'Nivel 5'];
  const nivel = nivelDesdeVentasEnVentana(v);
  if (nivel >= 5) {
    return {
      nivel: 5,
      labelActual: labels[4],
      siguienteLabel: null,
      actual: v,
      siguienteUmbral: null,
      barPct: 100,
    };
  }
  const nextAt = [UMBRAL_NIVEL_2, UMBRAL_NIVEL_3, UMBRAL_NIVEL_4, UMBRAL_NIVEL_5];
  const prevAt = [0, UMBRAL_NIVEL_2, UMBRAL_NIVEL_3, UMBRAL_NIVEL_4];
  const next = nextAt[nivel - 1];
  const prev = prevAt[nivel - 1];
  const barPct = Math.min(100, Math.round(((v - prev) / (next - prev)) * 100));
  return {
    nivel,
    labelActual: labels[nivel - 1],
    siguienteLabel: labels[nivel],
    actual: v,
    siguienteUmbral: next,
    barPct: Number.isFinite(barPct) ? barPct : 0,
  };
}
